import { Component, OnInit } from '@angular/core';
import { AdminService, NotificationConfig, NotificationType } from '../services/admin/admin.service';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-notificaciones',
  templateUrl: './notificaciones.component.html',
  styleUrls: ['./notificaciones.component.css'],
})
export class NotificacionesComponent implements OnInit {
  constructor(
    private admin: AdminService,
    private activeRoute: ActivatedRoute
  ) { }

  /**
   * Determina si los datos a mostrar han sido cargados y están listos para mostrar.
   */
  isDataLoaded = false;
  showCreateOptions = false;
  /**
   * Lista completa de configuraciones obtenidas de la API
   */
  notificationConfigs: NotificationConfig[] = [];

  /**
   * Configuraciones filtradas por tipo (notificaciones vs procesos)
   */
  notificationsList: NotificationConfig[] = [];
  processesList: NotificationConfig[] = [];

  showCreateModal = false;
  newConfigForm = {
    tipo_notificacion: '',
    activo: true,
    dias_frecuencia: 7,
    hora_envio: '09:00:00',
    dias_anticipacion: 1
  };

  // Tipos de notificación con configuraciones por defecto
  notificationTypeDefaults = {
    'puntos_vencer': {
      display: 'Puntos por Vencer',
      dias_frecuencia: 7,
      hora_envio: '09:00:00',
      dias_anticipacion: 5
    },
    'perfil_incompleto': {
      display: 'Completar Perfil',
      dias_frecuencia: 14,
      hora_envio: '10:00:00',
      dias_anticipacion: 1
    },
    'logros_pendientes': {
      display: 'Logros Pendientes',
      dias_frecuencia: 5,
      hora_envio: '16:00:00',
      dias_anticipacion: 1
    },
    'encuestas_pendientes': {
      display: 'Encuestas Pendientes',
      dias_frecuencia: 3,
      hora_envio: '11:00:00',
      dias_anticipacion: 1
    }
  };

  /**
   * Tipos de notificación disponibles
   */
  availableNotificationTypes: NotificationType[] = [
    {
      key: 'puntos_vencer',
      display: 'Puntos por Vencer',
      description: 'Notificar a usuarios que tengan puntos por vencer',
      defaultConfig: {
        dias_frecuencia: 7,
        hora_envio: '09:00:00',
        dias_anticipacion: 5
      }
    },
    {
      key: 'perfil_incompleto',
      display: 'Perfil Incompleto',
      description: 'Notificar a usuarios con perfil incompleto',
      defaultConfig: {
        dias_frecuencia: 14,
        hora_envio: '10:00:00',
        dias_anticipacion: 1
      }
    },
    {
      key: 'logros_pendientes',
      display: 'Logros Pendientes',
      description: 'Notificar sobre logros pendientes por reclamar',
      defaultConfig: {
        dias_frecuencia: 5,
        hora_envio: '16:00:00',
        dias_anticipacion: 1
      }
    },
    {
      key: 'encuestas_pendientes',
      display: 'Encuestas Pendientes',
      description: 'Notificar sobre encuestas pendientes',
      defaultConfig: {
        dias_frecuencia: 3,
        hora_envio: '11:00:00',
        dias_anticipacion: 1
      }
    }
  ];

  /**
   * Tipos de proceso disponibles
   */
  availableProcessTypes: NotificationType[] = [
    {
      key: 'marcar_inactivos',
      display: 'Marcar Usuarios Inactivos',
      description: 'Marcar usuarios como inactivos automáticamente [Diario]',
      defaultConfig: {
        dias_frecuencia: 1,
        hora_envio: '02:00:00',
        dias_anticipacion: 0
      }
    }
  ];

  /**
   * Estados de carga para cada operación
   */
  loadingStates: { [key: string]: boolean } = {};

  /**
   * Modal de configuración
   */
  showConfigModal = false;
  showEditModal = false;
  showDeleteModal = false;
  selectedNotificationType: NotificationType | null = null;
  selectedConfig: NotificationConfig | null = null;
  configToDelete: NotificationConfig | null = null;
  isEditMode = false;
  configForm = {
    dias_frecuencia: 7,
    hora_envio: '09:00:00',
    dias_anticipacion: 1,
    activo: true
  };

  ngOnInit() {
    this.loadNotificationConfigurations();
  }

  /**
   * Carga todas las configuraciones desde la API
   */
  loadNotificationConfigurations() {
    this.isDataLoaded = false;

    this.admin.getNotificationConfigurations().subscribe({
      next: (response: any) => {
        if (response.success) {
          this.notificationConfigs = response.data;
          this.categorizeConfigurations();
        }
        this.isDataLoaded = true;
      },
      error: (error) => {
        console.error('Error loading notification configurations:', error);
        this.isDataLoaded = true;
      }
    });
  }

  /**
   * Categoriza las configuraciones en notificaciones y procesos
   */
  categorizeConfigurations() {
    this.notificationsList = this.notificationConfigs.filter(config =>
      this.availableNotificationTypes.some(type => type.key === config.tipo_notificacion)
    );

    this.processesList = this.notificationConfigs.filter(config =>
      this.availableProcessTypes.some(type => type.key === config.tipo_notificacion)
    );
  }

  /**
   * Obtiene la información de display para un tipo de notificación
   */
  getNotificationTypeInfo(tipoNotificacion: string): NotificationType | undefined {
    const allTypes = this.availableNotificationTypes.concat(this.availableProcessTypes);
    return allTypes.find(type => type.key === tipoNotificacion);
  }

  /**
   * Alterna el estado activo/inactivo de una configuración
   */
  toggleNotificationState(config: NotificationConfig) {
    const loadingKey = 'toggle_' + config.id;
    this.loadingStates[loadingKey] = true;

    this.admin.toggleNotificationConfiguration(config.id).subscribe({
      next: (response) => {
        if (response.success) {
          // Actualizar el estado local
          config.activo = response.data.activo;
        }
        this.loadingStates[loadingKey] = false;
      },
      error: (error) => {
        console.error('Error toggling notification state:', error);
        this.loadingStates[loadingKey] = false;
      }
    });
  }

  /**
   * Abre el modal para configurar una nueva notificación
   */
  openConfigModal(notificationType: NotificationType) {
    this.selectedNotificationType = notificationType;
    this.isEditMode = false;
    this.selectedConfig = null;

    // Cargar valores por defecto
    this.configForm = {
      dias_frecuencia: notificationType.defaultConfig.dias_frecuencia,
      hora_envio: notificationType.defaultConfig.hora_envio,
      dias_anticipacion: notificationType.defaultConfig.dias_anticipacion,
      activo: true
    };

    this.showConfigModal = true;
  }

  /**
   * Abre el modal para editar una configuración existente
   */
  openEditModal(config: NotificationConfig) {
    this.selectedConfig = config;
    this.isEditMode = true;

    // Buscar el tipo de notificación para mostrar información
    this.selectedNotificationType = this.getNotificationTypeInfo(config.tipo_notificacion) || null;

    // Cargar valores actuales
    this.configForm = {
      dias_frecuencia: config.dias_frecuencia,
      hora_envio: config.hora_envio,
      dias_anticipacion: config.dias_anticipacion,
      activo: config.activo
    };

    this.showConfigModal = true;
  }

  /**
   * Cierra el modal de configuración
   */
  closeConfigModal() {
    this.showConfigModal = false;
    this.selectedNotificationType = null;
    this.selectedConfig = null;
    this.isEditMode = false;
  }

  /**
   * Crea una nueva configuración con los valores del formulario
   */
  createNotificationWithConfig() {
    if (!this.selectedNotificationType) return;

    const loadingKey = 'create_' + this.selectedNotificationType.key;
    this.loadingStates[loadingKey] = true;

    const newConfig = {
      tipo_notificacion: this.selectedNotificationType.key,
      activo: this.configForm.activo,
      dias_frecuencia: this.configForm.dias_frecuencia,
      hora_envio: this.configForm.hora_envio,
      dias_anticipacion: this.configForm.dias_anticipacion
    };

    this.admin.createNotificationConfiguration(newConfig).subscribe({
      next: (response) => {
        if (response.success) {
          this.loadNotificationConfigurations();
          this.closeConfigModal();
          // Aquí podrías mostrar un mensaje de éxito
          console.log('Configuración creada exitosamente');
        }
        this.loadingStates[loadingKey] = false;
      },
      error: (error) => {
        console.error('Error creating notification configuration:', error);
        this.loadingStates[loadingKey] = false;
        // Aquí podrías mostrar un mensaje de error
      }
    });
  }

  /**
   * Actualiza una configuración existente con los valores del formulario
   */
  updateNotificationWithConfig() {
    if (!this.selectedConfig) return;

    const loadingKey = 'update_' + this.selectedConfig.id;
    this.loadingStates[loadingKey] = true;

    const updateConfig = {
      dias_frecuencia: this.configForm.dias_frecuencia,
      hora_envio: this.configForm.hora_envio,
      dias_anticipacion: this.configForm.dias_anticipacion,
      activo: this.configForm.activo
    };

    this.admin.patchNotificationConfiguration(this.selectedConfig.id, updateConfig).subscribe({
      next: (response) => {
        if (response.success) {
          this.loadNotificationConfigurations();
          this.closeConfigModal();
          // Aquí podrías mostrar un mensaje de éxito
          console.log('Configuración actualizada exitosamente');
        }
        this.loadingStates[loadingKey] = false;
      },
      error: (error) => {
        console.error('Error updating notification configuration:', error);
        this.loadingStates[loadingKey] = false;
        // Aquí podrías mostrar un mensaje de error
      }
    });
  }

  /**
   * Guarda la configuración (crear o actualizar según el modo)
   */
  saveNotificationConfig() {
    if (this.isEditMode) {
      this.updateNotificationWithConfig();
    } else {
      this.createNotificationWithConfig();
    }
  }

  /**
   * Elimina una configuración de notificación
   */
  deleteNotificationConfiguration(config: NotificationConfig) {
    this.configToDelete = config;
    this.showDeleteModal = true;
  }

  /**
   * Confirma la eliminación de la configuración
   */
  confirmDelete() {
    if (!this.configToDelete) return;

    const loadingKey = 'delete_' + this.configToDelete.id;
    this.loadingStates[loadingKey] = true;

    this.admin.deleteNotificationConfiguration(this.configToDelete.id).subscribe({
      next: (response) => {
        if (response.success) {
          this.loadNotificationConfigurations();
          console.log('Configuración eliminada exitosamente');
        }
        this.loadingStates[loadingKey] = false;
        this.closeDeleteModal();
      },
      error: (error) => {
        console.error('Error deleting notification configuration:', error);
        this.loadingStates[loadingKey] = false;
        this.closeDeleteModal();
        // Aquí podrías mostrar un mensaje de error
      }
    });
  }

  openCreateNotificationModal() {
    this.showCreateModal = true;
    this.newConfigForm = {
      tipo_notificacion: '',
      activo: true,
      dias_frecuencia: 7,
      hora_envio: '09:00:00',
      dias_anticipacion: 1
    };
  }

  getAvailableNotificationTypes() {
    return Object.keys(this.notificationTypeDefaults).map(key => ({
      key: key,
      display: this.notificationTypeDefaults[key].display
    }));
  }


  onNotificationTypeChange() {
    if (this.newConfigForm.tipo_notificacion) {
      const defaults = this.notificationTypeDefaults[this.newConfigForm.tipo_notificacion];
      if (defaults) {
        this.newConfigForm.dias_frecuencia = defaults.dias_frecuencia;
        this.newConfigForm.hora_envio = defaults.hora_envio;
        this.newConfigForm.dias_anticipacion = defaults.dias_anticipacion;
      }
    }
  }

  closeCreateModal() {
    this.showCreateModal = false;
  }

  createConfiguration() {
    if (!this.newConfigForm.tipo_notificacion) {
      return;
    }

    this.admin.createNotificationConfiguration(this.newConfigForm).subscribe({
      next: (response) => {
        if (response.success) {
          this.loadNotificationConfigurations();
          this.closeCreateModal();
        }
      },
      error: (error) => {
        console.error('Error:', error);
      }
    });
  }

  /**
   * Cierra el modal de confirmación de eliminación
   */
  closeDeleteModal() {
    this.showDeleteModal = false;
    this.configToDelete = null;
  }

  /**
   * Valida el formulario de configuración
   */
  isFormValid(): boolean {
    return this.configForm.dias_frecuencia > 0 &&
      this.configForm.hora_envio !== '' &&
      this.configForm.dias_anticipacion >= 0;
  }

  /**
   * Obtiene opciones de frecuencia predefinidas
   */
  getFrequencyOptions() {
    return [
      { value: 1, label: 'Diario' },
      { value: 3, label: 'Cada 3 días' },
      { value: 7, label: 'Semanal' },
      { value: 14, label: 'Quincenal' },
      { value: 30, label: 'Mensual' }
    ];
  }
  isNotificationTypeConfigured(notificationType: string): boolean {
    return this.notificationConfigs.some(config => config.tipo_notificacion === notificationType);
  }

  /**
   * Obtiene tipos de notificación no configurados
   */
  getUnconfiguredNotificationTypes(): NotificationType[] {
    return this.availableNotificationTypes.filter(type =>
      !this.isNotificationTypeConfigured(type.key)
    );
  }

  /**
   * Obtiene tipos de proceso no configurados
   */
  getUnconfiguredProcessTypes(): NotificationType[] {
    return this.availableProcessTypes.filter(type =>
      !this.isNotificationTypeConfigured(type.key)
    );
  }

  /**
   * Verifica si hay una operación en curso
   */
  isLoading(key: string): boolean {
    return this.loadingStates[key] || false;
  }

  /**
   * Formatea la fecha para mostrar
   */
  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  }

  /**
   * Obtiene la frecuencia para mostrar
   */
  getFrequencyDisplay(diasFrecuencia: number): string {
    switch (diasFrecuencia) {
      case 1: return 'Diario';
      case 7: return 'Semanal';
      case 14: return 'Quincenal';
      case 30: return 'Mensual';
      default: return 'Cada ' + diasFrecuencia + ' días';
    }
  }
}
